/** @jsxImportSource @emotion/react */
import React, { FC } from 'react';
import styled from '@emotion/styled/macro';
import { css, Theme } from '@emotion/react';
import { VscArrowRight, VscArrowLeft } from 'react-icons/vsc';
import { Trans, useTranslation } from 'react-i18next';

import Text from 'components/styled/Text';
import { Flex, Box, Grid } from 'components/styled';
import Button, {
	NavButton,
	NavLinkButton,
	NavHrefButton,
} from 'components/styled/Button';
import ModalDialog from 'components/modal';
import Paper from 'components/styled/Paper';
import { HrefLink } from 'components/styled/Link';
import SocialLink, { LinkLabels } from 'components/profile/SocialLink';

import { TitleTextCustom } from 'modules/public/homepage/zlin';

import { useLocalizedMultistring } from 'utils/localization';
import { EASMultilanguage } from 'utils/EASTypes';

import ambassador1 from '../../assets/zlin/ambassadors/ambassador_nikol_ungvari.jpg';
import ambassador2 from '../../assets/zlin/ambassadors/ambassador_monika_belohlavkova.jpg';
// import ambassador3 from '../../assets/zlin/ambassadors/';
import ambassador4 from '../../assets/zlin/ambassadors/ambassador_ondrej_svacina.jpg';
import ambassador5 from '../../assets/zlin/ambassadors/ambassador_vanda_klirova.jpg';
import ambassador6 from '../../assets/zlin/ambassadors/ambassador_nicola_nagyova.jpg';
import ambassadorPlaceholder from '../../assets/zlin/ambassadors/portrait-placeholder.png';

const TextCustomHeadline = styled(Text)`
	font-weight: bold;
	color: secondary;
	text-transform: uppercase;
	margin-top: 5px;
`;

export const Advertising: FC = () => {
	const localize = useLocalizedMultistring();
	const { i18n } = useTranslation();
	return (
		<Box>
			<Text>
				{localize({
					CZECH:
						'Každá firma má možnost registrovat se do našeho Job Portalu, který propojuje studenty s firmami. Job Portal Vám umožní nahrát informace o firmě a aktuální pracovní pozice, které byste rádi obsadili. Studenti zase nahrají veškeré své pracovní zkušenosti, stejně tak jako další dovednosti a důležité údaje. Následně Vám Job Portal vygeneruje seznam studentů s nejvyšší shodou.',
					ENGLISH:
						'Each company may register with TBU Job Portal, which facilitates interconnection of students and companies. The Job Portal will enable you to upload information about the company and current job vacancies. Students may upload information about all their work experience, as well as other skills and important facts. The Job Portal will consequently generate a list of students with the highest match score.',
				})}
			</Text>
			<Text>
				{localize({
					CZECH:
						'Při registraci do Job Portalu si můžete zvolit z několika nabídek. Nabízíme různé možnosti, od krátkodobého zavedení firmy, jednorázové pracovní nabídky, po roční předplatné Portalu a rozšíření funkcí. Pro detailní informace nás neváhejte kontaktovat.',
					ENGLISH:
						'When registering with the Job Portal, you may select from several options, ranging from short-term registration of the company with the Portal and creation of the company’s profile in the Portal, one-time vacancies, to a year-long subscription of Portal and extension of the range of functions. Please do not hesitate to contact us for detailed information.',
				})}
			</Text>
			{/* they wanted to comment this 14.2.2023 */}
			{/* <Text fontWeight="bold">
				{localize({
					CZECH:
						'Nově zaregistrovaným firmám nabízíme kompletní balíček služeb na 3 měsíce zdarma!',
					ENGLISH:
						'We offer newly registered companies Complete package for 3 months free!',
				})}
			</Text> */}
			<NavLinkButton to="/public/contact" variant="primary" mt={3}>
				{localize({
					CZECH: 'Chci se dozvědět více -  kontaktujte nás.',
					ENGLISH: 'I want to learn more - contact us.',
				})}
			</NavLinkButton>
			<NavHrefButton
				ml={3}
				variant="primary"
				href={i18n.language === 'cz' ? '/cenik_cz.pdf' : '/cenik_en.pdf'}
				target="_blank"
			>
				{localize({
					CZECH: 'Aktuální ceník platný od 1. 1. 2025',
					ENGLISH: 'Current price list valid from January 1, 2025',
				})}
			</NavHrefButton>

			{/*
			<Text mt={3}>
				{localize({
					CZECH: 'Pokud máte zájem o dlouhodobou spolupráci a výhody, klikněte',
					ENGLISH:
						'If you are interested in long-term cooperation and benefits, please click',
				})}{' '}

				<HrefLink color="primary" href="/"

				// TODO: link to /partnerstvi, zatial deaktivovane
				>
					{localize({
						CZECH: 'zde',
						ENGLISH: 'here',
					})}
				</HrefLink>
				.
			</Text> */}
		</Box>
	);
};

export const Collaboration: FC = () => {
	const localize = useLocalizedMultistring();
	return (
		<Flex flexDirection={['column', 'column', 'row']}>
			<Box width={[1, 1, 1 / 2]} mr={[0, 4]}>
				<TextCustomHeadline color="primary" fontSize="lg">
					{localize({
						CZECH: 'Uzavření smlouvy o partnerství',
						ENGLISH: 'Conclusion of a partnership agreement',
					})}
				</TextCustomHeadline>
				<Text mt={2} mb={3}>
					{localize({
						CZECH:
							'Chcete dlouhodobě spolupracovat s Univerzitou Tomáše Bati ve Zlíně, nabízíme Vám uzavření smlouvy s UTB. Na vyžádání Vám zašleme vzorovou smlouvu, kterou lze do určité míry modifikovat dle Vašich možností.',
						ENGLISH:
							'Do you want to cooperate with Tomas Bata University in Zlín in the long-term? If so, you can conclude an agreement with TBU. We can send you the agreement template upon request, which can be modified to a certain extent if required.',
					})}
				</Text>
				<Text fontWeight="bold">
					{localize({
						CZECH: 'Uzavřením smlouvy se můžete zapojit do aktivit jako',
						ENGLISH:
							'By concluding the agreement you can get involved in activities such as',
					})}
					:
				</Text>
				<Text my={0}>
					-{' '}
					{localize({
						CZECH: 'Realizace odborných praxí/stáží pro studenty UTB',
						ENGLISH:
							'Implementation of specialized internship/traineeship for TBU students',
					})}
				</Text>
				<Text my={0}>
					-{' '}
					{localize({
						CZECH: 'Účast na odborných přednáškách',
						ENGLISH: 'Participation in specialized lectures',
					})}
				</Text>
				<Text my={0}>
					-{' '}
					{localize({
						CZECH: 'Focus group se studenty UTB',
						ENGLISH: 'Focus group with TBU students',
					})}
				</Text>
				<Text my={0}>
					-{' '}
					{localize({
						CZECH:
							'Pořádání rozvojových kurzů pro studenty s firmou jako sponzorem kurzu',
						ENGLISH:
							'Organization of development courses for students with the company as the event sponsor',
					})}
				</Text>
				<Text my={0}>
					-{' '}
					{localize({
						CZECH: 'Assessment centrum se studenty UTB',
						ENGLISH: 'Assessment centre with TBU students',
					})}
				</Text>
				{/*
				<Text my={0}>
					-{' '}
					{localize({
						CZECH: 'Účast firmy v roli partnera',
						ENGLISH:
							'Participation of the company as partner to the institutional',
					})}{' '}
					<HrefLink
						color="primary"
						href="/"
						target="_blank"
						//TODO: link na video z posledni akce, zatial deaktivovane
					>
						{localize({
							CZECH: 'colouniverzitního Seznamováku UTB',
							ENGLISH: 'TBU Get-Together Camp',
						})}
					</HrefLink>
				</Text> */}
			</Box>
			<Box width={[1, 1, 1 / 2]} ml={[0, 0, 4]} mt={[4, 4, 0]}>
				<TextCustomHeadline color="primary" fontSize="lg">
					{localize({
						CZECH: 'Přístup do Job Portalu',
						ENGLISH: 'Access to the Job Portal',
					})}
				</TextCustomHeadline>
				<Text mt={2} mb={4}>
					{localize({
						CZECH:
							'Pokud chcete zřídit přístup do Job Portalu s přístupem k CV studentů a absolventů na Vámi hledané pracovní pozice a možnost inzerovat své pracovní nabídky klikněte',
						ENGLISH:
							'If you want to be provided access to the Job Portal in order to view the CVs of the candidates for the job positions you offer from among TBU students and graduates and to be able to advertise your job vacancies, please click',
					})}{' '}
					<HrefLink color="primary" href="/login/company">
						{localize({
							CZECH: 'zde',
							ENGLISH: 'here',
						})}
					</HrefLink>
					.
				</Text>
				<TextCustomHeadline color="primary" fontSize="lg">
					{localize({
						CZECH: 'Další spolupráce',
						ENGLISH: 'Other forms of cooperation',
					})}
				</TextCustomHeadline>
				<Text mt={2} mb={4}>
					<HrefLink
						color="primary"
						href="https://www.utb.cz/spoluprace/firmy/"
						target="_blank"
					>
						https://www.utb.cz/spoluprace/firmy/
					</HrefLink>
				</Text>
			</Box>
		</Flex>
	);
};

export const Events: FC = () => {
	const localize = useLocalizedMultistring();
	return (
		<>
			<Text mb={3}>
				{localize({
					CZECH:
						'Chcete rozšířit povědomí o vaší firmě nebo si vybrat toho nejschopnějšího zaměstnance? Pro tyto případy zprostředkujeme osobní kontakt přímo se studenty.',
					ENGLISH:
						'Do you want to raise awareness of your company or select the best candidate for the position? For these cases, we can facilitate personal contact directly with students.',
				})}
			</Text>
			<TextCustomHeadline color="primary" fontSize="lg">
				{localize({
					CZECH: 'Jaké jsou možnosti',
					ENGLISH: 'The possibilities are as follows',
				})}
			</TextCustomHeadline>
			<Flex flexDirection={['column', 'column', 'row']}>
				<Box width={[1, 1, 1 / 2]} mr={[0, 4]}>
					<TextCustomHeadline>
						{localize({
							CZECH:
								'Setkání na Veletrhu pracovních příležitostí – Business Day',
							ENGLISH:
								'Meeting at the Career Opportunities Fair – Business Day',
						})}
					</TextCustomHeadline>
					<Text mt={2} mb={3}>
						{localize({
							CZECH:
								'Cílem je setkání studentů s potenciálními zaměstnavateli. Zde máte ideální šanci získat schopné studenty dříve než jiné společnosti a navázat s nimi kontakt. Více na',
							ENGLISH:
								'The aim is to facilitate contact between students and prospective employers. This is an ideal opportunity for you to attract the attention of talented students and establish contact with them earlier than other companies. More information is available at',
						})}{' '}
						<HrefLink
							href="https://businessdays.utb.cz/"
							target="_blank"
							color="primary"
						>
							www.businessdays.utb.cz
						</HrefLink>
						.
					</Text>
					<TextCustomHeadline>
						{localize({
							CZECH: 'Prezentace nebo workshop na UTB',
							ENGLISH: 'Presentation or workshop at TBU',
						})}
					</TextCustomHeadline>
					<Text mt={2} mb={3}>
						{localize({
							CZECH:
								'Chcete šířit své jméno a dostat se do kontaktu se studenty? Zajistíme Vám prostory a zpropagujeme akci.',
							ENGLISH:
								'Do you want to make your company more visible to students and enter into contact with them? We will provide a venue for your event and promote it.',
						})}
					</Text>
					<TextCustomHeadline>
						{localize({
							CZECH: 'Stáž nebo trainee program',
							ENGLISH: 'Internship or a trainee programme',
						})}
					</TextCustomHeadline>
					<Text mt={2} mb={3}>
						{localize({
							CZECH:
								'Ideální šance, jak si odchytit šikovné studenty. Zašlete nám nabídku a my ji zveřejníme mezi studenty.',
							ENGLISH:
								'An ideal chance to recruit talented students. Send us the list of vacancies offered and we will make it available to students.',
						})}
					</Text>
				</Box>
				<Box width={[1, 1, 1 / 2]} ml={[0, 0, 4]}>
					<TextCustomHeadline>
						{localize({
							CZECH: 'Prezentace na Seznamováku UTB',
							ENGLISH: 'Presentation at TBU Get-Together Camp',
						})}
					</TextCustomHeadline>
					<Text mt={2} mb={3}>
						{localize({
							CZECH:
								'Místo prvního kontaktu studenta s univerzitou, kdy je otevřen všem novým informacím. Co takhle se mu představit?',
							ENGLISH:
								'Place of students’ first contact with the University, when their minds are open to any new information. What about presenting your company to them?',
						})}
					</Text>
					<TextCustomHeadline>
						{localize({
							CZECH: 'Mentoring studentů',
							ENGLISH: 'Mentoring of students',
						})}
					</TextCustomHeadline>
					<Text mt={2} mb={3}>
						{localize({
							CZECH:
								'Zveme odborníky z praxe, kteří jsou ochotni předávat své zkušenosti a pomáhat s rozvojem studentů.',
							ENGLISH:
								'We invite experts from business and industry who are willing to share their experience and help develop students’ skills.',
						})}{' '}
						<HrefLink
							color="primary"
							href="https://calendar.jobcentrum.utb.cz"
							target="_blank"
						>
							{localize({
								CZECH: 'Mentoring',
								ENGLISH: 'Mentoring',
							})}{' '}
						</HrefLink>
						{localize({
							CZECH:
								'je skvělá šance, jak se poznat s proaktivními studenty a pomoct jim na jejich kariérní cestě.',
							ENGLISH:
								'is a great opportunity to get to know proactive students and help them advance in their career.',
						})}
					</Text>
					<TextCustomHeadline>
						{localize({
							CZECH: 'Exkurze ve firmách',
							ENGLISH: 'Excursions to companies',
						})}
					</TextCustomHeadline>
					<Text mt={2} mb={3}>
						{localize({
							CZECH:
								'Chcete ukázat studentům, jak to u vás ve firmě vypadá a funguje? Není nic lepšího než se domluvit na exkurzi přímo u vás.',
							ENGLISH:
								'Do you want to get students acquainted with your company and its operation? There is nothing easier than arranging an excursion in your company.',
						})}
					</Text>
				</Box>
			</Flex>
		</>
	);
};

export const News: FC = () => {
	const { t } = useTranslation('homepage/modal_cards/news/');
	return (
		<>
			<Text>{t('p1')}</Text>
			<Text>
				{t('p2')}{' '}
				<HrefLink color="primary" href={t('mail_link')}>
					{t('mail_label')}
				</HrefLink>
				.
			</Text>
			<Text mt={4}>{t('p3')} </Text>
		</>
	);
};

export const Experience: FC = () => {
	const { t } = useTranslation('homepage/modal_cards/experience/');
	return (
		<>
			<Text>{t('p1')}</Text>
			<Box mb={2}>
				<HrefLink color="primary" href={t('link1_href')} download>
					{t('link1_label')}
				</HrefLink>
			</Box>
			<HrefLink color="primary" href={t('link2_href')} download>
				{t('link2_label')}
			</HrefLink>
			<br />
			<HrefLink color="primary" href={t('link3_href')} download>
				{t('link3_label')}
			</HrefLink>
			<br />
			<HrefLink color="primary" href={t('link4_href')} download>
				{t('link4_label')}
			</HrefLink>
			<br />
			<HrefLink color="primary" href={t('link5_href')} download>
				{t('link5_label')}
			</HrefLink>
			<Box mt={2}>
				<HrefLink color="primary" href={t('link6_href')} download>
					{t('link6_label')}
				</HrefLink>
			</Box>
			<Text mt={3}>
				<Trans>{t('p2')}</Trans>
			</Text>
		</>
	);
};

export const Diagnostics: FC = () => {
	const { t } = useTranslation('homepage/modal_cards/diagnostics/');
	return (
		<>
			<Text>{t('p1')}</Text>
			<Text>{t('p2')}</Text>
			<NavHrefButton
				href={t('link1_url')}
				target="_blank"
				variant="primary"
				mt={3}
			>
				{t('link1_label')}
			</NavHrefButton>
		</>
	);
};

export const Coaching: FC = () => {
	const { t } = useTranslation('homepage/modal_cards/coaching/');
	return (
		<>
			<Text>{t('p1')}</Text>
			<Text>{t('p2')}</Text>
			<Text mt={4}>
				{t('p3')}{' '}
				<HrefLink color="primary" target="_blank" href={t('link1_url')}>
					{t('link1_label')}
				</HrefLink>
				<br />
				{t('p4')}{' '}
				<HrefLink color="primary" target="_blank" href={t('link2_url')}>
					{t('link2_label')}
				</HrefLink>
			</Text>
		</>
	);
};
export const Jobfair: FC = () => {
	const { t } = useTranslation('homepage/modal_cards/jobfair/');
	return (
		<>
			<Text>{t('p1')}</Text>
			<Text>{t('p2')}</Text>
			<Text mt={4}>
				{t('p3')}{' '}
				<HrefLink color="primary" href={t('link1_url')} target="_blank">
					{t('link1_label')}
				</HrefLink>
			</Text>
		</>
	);
};

export const Internships: FC = () => {
	const { t } = useTranslation('homepage/modal_cards/interships/student/');
	return (
		<Flex flexDirection={['column', 'column', 'row']}>
			{/**students */}
			<Box width={[1, 1, 1 / 2]} mr={[0, 4]}>
				<TextCustomHeadline color="primary" fontSize="lg">
					{t('subtitle')}
				</TextCustomHeadline>
				<Text mt={2} mb={4}>
					{t('p1')}
				</Text>
				<TextCustomHeadline>{t('p2')}</TextCustomHeadline>
				<Trans>
					<Text>{t('p3')}</Text>
					<Text>{t('p4')}</Text>
					<Text mt={4}>{t('p5')}</Text>
					<Text>{t('p6')}</Text>
					<Text>{t('p7')}</Text>
					<Text mt={4}>
						{t('p8')}{' '}
						<HrefLink href={t('mail_url')} color="primary">
							{t('mail_label')}
						</HrefLink>
					</Text>{' '}
					{t('p9')}
				</Trans>
				<Text>
					{t('p10')}
					<HrefLink href={t('link1_url')} color="primary" target="_blank">
						{t('link1_label')}
					</HrefLink>
				</Text>
			</Box>
		</Flex>
	);
};

export const Mentoring: FC = () => {
	const { t } = useTranslation('homepage/modal_cards/mentoring/');
	return (
		<>
			<Text>{t('p1')}</Text>
			<Text>{t('p2')}</Text>
			<Text mt={4}>
				{t('p3')}{' '}
				<HrefLink color="primary" target="_blank" href={t('link1_url')}>
					{t('link1_label')}
				</HrefLink>
				.
			</Text>
		</>
	);
};

export const Workshops: FC = () => {
	const { t } = useTranslation('homepage/modal_cards/workshops/');
	return (
		<>
			<Text>{t('p1')}</Text>
			<Text>
				{t('p2')} <b>{t('p3')}</b> {t('p4')}{' '}
				<HrefLink color="primary" href={t('mail_link')}>
					{t('mail_label')}
				</HrefLink>{' '}
				{t('p5')}
			</Text>
			<NavHrefButton
				href={t('link1_url')}
				target="_blank"
				variant="primary"
				mt={3}
			>
				{t('link1_label')}
			</NavHrefButton>
		</>
	);
};

export const CareerCounseling: FC = () => {
	const { t } = useTranslation('homepage/modal_cards/careercounseling/');
	return (
		<>
			<Text>{t('p1')}</Text>
			<Text pb={3}>{t('p2')}</Text>
			<TextCustomHeadline>{t('p3')}</TextCustomHeadline>
			<Box mb={3} pb={2}>
				<HrefLink href={t('link1_url')} download color="primary">
					{t('link1_label')}
				</HrefLink>
				<br />
				<HrefLink href={t('link2_url')} download color="primary">
					{t('link2_label')}
				</HrefLink>
				<br />
				<Box mt={2}>
					<HrefLink href={t('link3_url')} download color="primary">
						{t('link3_label')}
					</HrefLink>
					<br />
					<HrefLink href={t('link4_url')} download color="primary">
						{t('link4_label')}
					</HrefLink>
				</Box>
			</Box>
			<TextCustomHeadline>{t('p4')}</TextCustomHeadline>
			<Text fontWeight="bold">{t('p5')}</Text>
			<Text>{t('p6')}</Text>
			<HrefLink href={t('mail_url')}>{t('mail_label')}</HrefLink>
			<br />
			<HrefLink href={t('phone_url')}>{t('phone_label')}</HrefLink>
		</>
	);
};

export const HomepageCardModal: FC<{
	title: string;
	card: React.FC;
	to: string;
}> = ({ title, card }) => {
	const Card = card;
	return (
		<ModalDialog
			label="Více informací"
			customCss={(theme: Theme) => css`
				padding: 0 !important;
				min-width: ${theme.breakpoints[2]};
				@media (max-width: ${theme.breakpoints[2]}) {
					width: 100% !important;
					min-width: unset;
				}
			`}
			control={openModal => (
				<NavButton p={0} fontSize="xl" variant="primary" onClick={openModal}>
					<Box py={1} mb={1}>
						<VscArrowRight />
					</Box>
				</NavButton>
			)}
		>
			{closeModal => (
				<Paper
					css={css`
						padding: 0;
					`}
				>
					<Box px={[3, 5]} py={[3, 5]}>
						<TitleTextCustom color="primary">{title}</TitleTextCustom>

						<Box mt={3}>
							<Card />
						</Box>
					</Box>
					<Flex alignSelf="flex-end" pr={[3, 5]} pb={[3, 5]}>
						<Button
							p={0}
							fontSize="xl"
							onClick={closeModal}
							variant="primary"
							width="fit-content"
						>
							<Box py={1} mb={1}>
								<VscArrowLeft />
							</Box>
						</Button>
					</Flex>
				</Paper>
			)}
		</ModalDialog>
	);
};

export const Ambassadors: FC = () => {
	const localize = useLocalizedMultistring();

	type TAmbassador = {
		photo: string;
		faculty: EASMultilanguage;
		name: string;
		description: EASMultilanguage;
		socialLink: {
			label: LinkLabels;
			href: string;
		};
	};

	const ambassadors: TAmbassador[] = [
		{
			photo: ambassador1,
			faculty: {
				CZECH: 'Fakulta multimediálních komunikací',
				ENGLISH: 'Faculty of Multimedia Communications',
			},
			name: 'Nikol Ungvári',
			description: {
				CZECH:
					'Niki je studentkou Marketingových komunikací. Má ráda dobrou kávu, cestování a pozitivní lidi.',
				ENGLISH:
					'Niki is a Marketing Communications student. She likes good coffee, travelling and positive people.',
			},
			socialLink: {
				label: 'Instagram',
				href: 'https://www.instagram.com/niki_ung?igsh=MTVrMTJoY2plemZraA==',
			},
		},
		{
			photo: ambassador2,
			faculty: {
				CZECH: 'Fakulta humanitních studií',
				ENGLISH: 'Faculty of Humanities',
			},
			name: 'Monika Bělohlávková',
			description: {
				CZECH:
					'Monika je studentkou Sociální pedagogiky. Je veselá, přátelská a v budoucnu chce pracovat jako učitelka.',
				ENGLISH:
					'Monika is a student of Social Pedagogy. She is cheerful, friendly and wants to work as a teacher in the future.',
			},
			socialLink: {
				label: 'Facebook',
				href: 'https://www.facebook.com/monika.belohlavkova.77',
			},
		},
		{
			photo: ambassadorPlaceholder,
			faculty: {
				CZECH: 'Fakulta aplikované informatiky',
				ENGLISH: 'Faculty of Applied Informatics',
			},
			name: '',
			description: {
				CZECH: '',
				ENGLISH: '',
			},
			socialLink: {
				label: 'Facebook',
				href: '',
			},
		},
		{
			photo: ambassador4,
			faculty: {
				CZECH: 'Fakulta technologická',
				ENGLISH: 'Faculty of Technology',
			},
			name: 'Ondřej Svačina',
			description: {
				CZECH:
					'Ondra studuje 3. ročník oboru Procesní inženýrství, má rád hory a také studentský život ve Zlíně.',
				ENGLISH:
					'Ondra is a 3rd year student of Process Engineering, he likes mountains and also student life in Zlin.',
			},
			socialLink: {
				label: 'Instagram',
				href: 'https://www.instagram.com/svacak/?igsh=cHl3YjQyNjM1cTQy',
			},
		},
		{
			photo: ambassador5,
			faculty: {
				CZECH: 'Fakulta logistiky a krizového řízení',
				ENGLISH: 'Faculty of Logistics and Crisis Management',
			},
			name: 'Vanda Klírová',
			description: {
				CZECH:
					'Vanda studuje Management rizik. Mimo školu ráda sportuje, výletuje a cestuje za gastronomií.',
				ENGLISH:
					'Vanda is studying Risk Management. Outside of school she enjoys sports, going on trips and traveling for gastronomy ',
			},
			socialLink: {
				label: 'Facebook',
				href: 'https://www.facebook.com/vanda.klirova',
			},
		},
		{
			photo: ambassador6,
			faculty: {
				CZECH: 'Fakulta managementu a ekonomiky',
				ENGLISH: 'Fakulty of Management and Economics',
			},
			name: 'Nicola Nagyová',
			description: {
				CZECH:
					'Nicola studuje třetí ročník oboru Ekonomika a management. Pochází z Vysočiny a mezi její koníčky patří sport, malování a cestování.',
				ENGLISH:
					'Nicola is in her third year of studing Economics and Management. She is from the Vysočina region and her hobbies include sports, paiting and traveling.',
			},
			socialLink: {
				label: 'Instagram',
				href: 'https://www.instagram.com/nicola_nagy?igsh=MWVybzE1eTB0bDZnNQ==',
			},
		},
	];

	return (
		<Grid
			css={(theme: Theme) => css`
				gap: ${theme.space[3]}px;
				color: black;

				@media (min-width: 640px) {
					grid-template-columns: 1fr 1fr;
					gap: ${theme.space[5]}px;
				}
			`}
		>
			{ambassadors.map((ambassador, i) => (
				<Box key={i} style={{ marginBottom: 20 }}>
					<Box mb={4}>
						<img
							src={ambassador.photo}
							alt={`Foto ${ambassador.name}`}
							max-width="100%"
							height="250vh"
						/>
					</Box>
					<TextCustomHeadline>
						{localize(ambassador.faculty)}
					</TextCustomHeadline>
					<TextCustomHeadline>{ambassador.name}</TextCustomHeadline>
					<Text>{localize(ambassador.description)}</Text>
					<SocialLink {...ambassador.socialLink} ml={0} />
				</Box>
			))}
		</Grid>
	);
};
